import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, RefreshCw, Send, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, dateTime } from '../lib/api';

const request=(method,body)=>({method,headers:{'content-type':'application/json'},body:JSON.stringify(body)});
const label=value=>String(value||'').replaceAll('_',' ').replace(/\b\w/g,m=>m.toUpperCase());

const initialForm=()=>({
  title:'Back Office customer referral',
  category:'account',
  action_type:'customer_support_review',
  urgency:'normal',
  assigned_to:'auto',
  customer_contact_required:false,
  reason:'',
});

export default function CustomerBackOfficePanel({customerId}){
  const navigate=useNavigate();
  const formRef=useRef(null);
  const[data,setData]=useState(null),[form,setForm]=useState(initialForm),[loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[notice,setNotice]=useState('');
  const base='/api/customers/'+encodeURIComponent(customerId)+'/back-office/referrals';

  const load=useCallback(async()=>{
    setLoading(true);
    try{setData(await api(base));}
    catch(error){setNotice('Error: '+error.message);}
    finally{setLoading(false);}
  },[base]);

  useEffect(()=>{void load();},[load]);
  useEffect(()=>{
    const focus=()=>window.setTimeout(()=>formRef.current?.querySelector('input,select,textarea')?.focus(),50);
    window.addEventListener('customer360-focus-back-office-referral',focus);
    return()=>window.removeEventListener('customer360-focus-back-office-referral',focus);
  },[]);

  const referrals=data?.referrals||[],summary=data?.summary||{},assignees=data?.assignees||[];
  const openReferrals=useMemo(()=>referrals.filter(row=>!['resolved','closed'].includes(String(row.status||'').toLowerCase())),[referrals]);

  const create=async event=>{
    event.preventDefault();
    if(form.title.trim().length<3){setNotice('Error: Enter a clear referral title.');return;}
    if(form.reason.trim().length<5){setNotice('Error: Record what Back Office needs to do and why.');return;}
    setBusy(true);setNotice('');
    try{
      const result=await api(base,request('POST',form));
      const notification=result.referral?.assignment_notification;
      const emailMessage=notification?.status==='sent'
        ?' Assignment email sent to '+(notification.staff_name||result.referral?.assigned_to_name||'the assignee')+'.'
        :notification?.status==='queued_for_retry'
          ?' The assignment email could not be sent immediately and has been queued for automatic retry.'
          :'';
      setNotice('Back Office referral '+(result.referral?.reference||'')+' created and assigned to '+(result.referral?.assigned_to_name||'Back Office')+'.'+emailMessage);
      setForm(initialForm());
      await load();
    }catch(error){setNotice('Error: '+error.message);}
    finally{setBusy(false);}
  };

  const set=(key,value)=>setForm(current=>({...current,[key]:value}));

  return <section className="customer-back-office-panel">
    <header className="c360-block-head">
      <div>
        <h3 className="govuk-heading-s">Back Office referrals</h3>
        <p className="govuk-body">Refer customer work that needs elevated access, investigation, account correction, identity support, billing review or other Back Office action.</p>
      </div>
      <button className="button secondary small govuk-button govuk-button--secondary" onClick={load} disabled={busy||loading}><RefreshCw size={14}/>Refresh</button>
    </header>

    {notice&&<div className={'operation-notice '+(notice.startsWith('Error:')?'error':'')}>{notice}</div>}

    <div className="customer-back-office-summary">
      <div><span>Open</span><strong>{Number(summary.open||0)}</strong><small>Current referrals</small></div>
      <div><span>Critical</span><strong>{Number(summary.critical||0)}</strong><small>Immediate attention</small></div>
      <div><span>Overdue</span><strong>{Number(summary.overdue||0)}</strong><small>Past internal target</small></div>
      <div><span>Total history</span><strong>{Number(summary.total||0)}</strong><small>All referrals</small></div>
    </div>

    <div className="customer-back-office-layout">
      <section className="customer-back-office-create" ref={formRef}>
        <header><span>NEW REFERRAL</span><h4>Refer customer to Back Office</h4><p className="govuk-body">This creates a governed Back Office work item linked directly to this Customer360 record.</p></header>
        <form onSubmit={create}>
          <label className="wide govuk-label"><span>Referral title</span><input className="govuk-input" required value={form.title} onChange={e=>set('title',e.target.value)} placeholder="Short description of the work required"/></label>

          <label className="govuk-label"><span>Category</span><select className="govuk-select" value={form.category} onChange={e=>set('category',e.target.value)}>
            <option value="account">Account</option>
            <option value="identity">Identity</option>
            <option value="portal">Portal / My Sousa Murray</option>
            <option value="security">Security</option>
            <option value="billing">Billing</option>
            <option value="collections">Collections</option>
            <option value="data_correction">Data correction</option>
            <option value="technical">Technical</option>
            <option value="other">Other</option>
          </select></label>

          <label className="govuk-label"><span>Requested action</span><select className="govuk-select" value={form.action_type} onChange={e=>set('action_type',e.target.value)}>
            <option value="customer_support_review">General Back Office review</option>
            <option value="change_sign_in_email">Change sign-in email</option>
            <option value="identity_linking">Fix identity / account linking</option>
            <option value="portal_access_fault">Investigate portal access</option>
            <option value="account_data_correction">Correct customer/account data</option>
            <option value="billing_review">Billing review</option>
            <option value="collections_review">Collections review</option>
            <option value="security_method_exception">Security method exception</option>
            <option value="technical_investigation">Technical investigation</option>
            <option value="other_account_support">Other Back Office work</option>
          </select></label>

          <label className="govuk-label"><span>Urgency</span><select className="govuk-select" value={form.urgency} onChange={e=>set('urgency',e.target.value)}>
            <option value="normal">Normal — 72h target</option>
            <option value="urgent">Urgent — 24h target</option>
            <option value="critical">Critical — 4h target</option>
          </select></label>

          <label className="govuk-label"><span>Assign to</span><select className="govuk-select" value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)}>
            <option value="auto">Automatic — senior Back Office</option>
            {assignees.map(person=><option key={person.id} value={person.id}>{person.name}{Number(person.open_count)>=0?' · '+Number(person.open_count||0)+' open':''}</option>)}
            <option value="unassigned">Unassigned</option>
          </select><small>Automatic assignment chooses the senior Back Office member with the lightest open workload and avoids assigning the referral back to the person raising it where possible.</small></label>

          <label className="wide govuk-label"><span>Reason / work required</span><textarea className="govuk-textarea" required rows="6" value={form.reason} onChange={e=>set('reason',e.target.value)} placeholder="Explain what the customer needs, what has already been checked or attempted, and exactly what Back Office needs to do."/></label>

          <label className="wide customer-back-office-check govuk-label"><input type="checkbox" checked={form.customer_contact_required} onChange={e=>set('customer_contact_required',e.target.checked)}/><span>Back Office needs to contact the customer</span></label>

          <div className="customer-back-office-form-footer">
            <span><AlertTriangle size={14}/>Do not record passwords, verification codes or full payment-card details.</span>
            <button className="button primary govuk-button" disabled={busy||form.title.trim().length<3||form.reason.trim().length<5}><Send size={14}/>{busy?'Creating…':'Create Back Office referral'}</button>
          </div>
        </form>
      </section>

      <section className="customer-back-office-current">
        <header><span>CURRENT WORK</span><h4>Back Office referrals for this customer</h4></header>
        {loading?<div className="c360-compact-empty"><strong>Loading referrals</strong><span>Reading the Back Office record…</span></div>:openReferrals.length?<div className="customer-back-office-list">{openReferrals.map(row=><article key={row.id}>
          <div className="customer-back-office-list-main"><strong>{row.reference} · {row.title}</strong><span>{label(row.category)} · {label(row.urgency)} · {label(row.status)}</span><small>{row.reason}</small><small>{row.assigned_to_name||'Unassigned'}{row.due_at?' · Target '+dateTime(row.due_at):''}</small></div>
          <button className="button secondary small govuk-button govuk-button--secondary" type="button" onClick={()=>navigate('/back-office/'+encodeURIComponent(row.id))}><ExternalLink size={13}/>Open</button>
        </article>)}</div>:<div className="c360-compact-empty"><strong>No open Back Office referrals</strong><span>This customer currently has no outstanding Back Office work.</span></div>}
        {referrals.length>openReferrals.length&&<div className="customer-back-office-history-note"><UserCog size={13}/><span>{referrals.length-openReferrals.length} completed referral{referrals.length-openReferrals.length===1?'':'s'} remain in the customer's Back Office history.</span></div>}
      </section>
    </div>
  </section>;
}
